import { Component } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormBuilder, FormGroup, FormControl, Validators, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { ContactAutocompleteService, ContactAutocompleteResult, ContactDetails } from 'src/app/services/contacts/contact-autocomplete.service';
import { IAddressOption } from 'src/app/freightmancerlvl99/freightmancerlvl99.models';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-pool-add-modal',
  templateUrl: './pool-add-modal.component.html',
  styleUrls: ['./pool-add-modal.component.scss']
})
export class PoolAddModalComponent {
  poolPointForm!: FormGroup;
  poolPointAddressOptions: IAddressOption[] = [];
  storeAddressOptions: IAddressOption[][] = [];

  constructor(
    private fb: FormBuilder, 
    private dialogRef: MatDialogRef<PoolAddModalComponent>, 
    private contactSvc: ContactAutocompleteService,
    private http: HttpClient,
    private toastr: ToastrService
  ){}

  ngOnInit(){
    this.poolPointForm = this.fb.group({
      pool_point_name: ['', [Validators.minLength(2), Validators.required]],
      pool_point_id: ['', [Validators.minLength(2), Validators.required]],
      pool_point_contact_uuid: ['', [Validators.required, uuidValidator()]],
      pool_point_address_uuid: ['', [Validators.required, uuidValidator()]],
      pool_point_contact_name: [''],
      clients: this.fb.array([])
    })
  }

  close(){
    this.dialogRef.close();
  }

  submit(){
    //console.log('pool point info submitted: ', this.poolPointForm.value);
    const postBody = this.poolPointForm.value;
    const url = `/api/pool_points/pool_points_ng.php`;

    this.http.post<any>(url, postBody).subscribe(
      response => {
        if (response.code && response.code === 200)
          this.toastr.success('Pool point created!');
        else
          this.toastr.error('Pool point could not be created')
      },
      error => {
        this.toastr.error('Pool point could not be created');
      }
    )
    this.close();
  }

  /* Autocomplete-related Functions */
  onPoolPointContactSelected(selected: ContactAutocompleteResult){
    this.poolPointForm.patchValue({
      pool_point_contact_uuid: selected.value,
      pool_point_contact_name: selected.label
    })

    this.contactSvc.getDetails(selected.value).subscribe((data: ContactDetails) => {
      // populate address options list
      this.poolPointAddressOptions = (data.addresses || []).map(addr => ({
        value: addr.contact_address_uuid,
        label: [addr.address, addr.address_extended, addr.locality, addr.region].filter(Boolean).join(', ')
      }));
      // set a default address
      if (this.poolPointAddressOptions.length){
        this.poolPointForm.get('pool_point_address_uuid')!.setValue(this.poolPointAddressOptions[0].value);
      }
    })
  }

  onClientContactSelected(selected: ContactAutocompleteResult, index: number){
    this.clients.controls[index].patchValue({
      pool_point_client_contact_uuid: selected.value,
      pool_point_client_contact_name: selected.label
    });
  }

  onStoreContactSelected(selected: ContactAutocompleteResult, clientIndex: number, storeIndex: number){
    this.getStores(this.asFormGroup(this.clients.controls[clientIndex])).controls[storeIndex].patchValue({
      pool_point_store_contact_uuid: selected.value,
      pool_point_store_contact_name: selected.label
    });

    this.contactSvc.getDetails(selected.value).subscribe((data: ContactDetails) => {
      // populate address options list
      this.storeAddressOptions[storeIndex] = (data.addresses || []).map(addr => ({
        value: addr.contact_address_uuid,
        label: [addr.address, addr.address_extended, addr.locality, addr.region].filter(Boolean).join(', ')
      }));

      // set a default address
      if (this.storeAddressOptions.length > 0){
        this.getStores(this.asFormGroup(this.clients.controls[clientIndex])).controls[storeIndex].patchValue({
          pool_point_store_address_uuid: this.storeAddressOptions[storeIndex][0].value,
          pool_point_store_address_text: this.storeAddressOptions[storeIndex][0].label
        });
      }
    })
  }

  /* Form Array Functions */

  get clients(): FormArray{
    return this.poolPointForm.get('clients') as FormArray;
  }

  getStores(client: FormGroup): FormArray{
      return client.get('stores') as FormArray;
  }

  asFormGroup(control: AbstractControl): FormGroup {
    return control as FormGroup;
  }

  addClient(): void{
    this.clients.push(this.fb.group({
      pool_point_client_name: ['', Validators.minLength(2)],
      pool_point_client_contact_uuid: ['', [Validators.required, uuidValidator()]],
      pool_point_client_contact_name: [''], // for user readability 
      stores: this.fb.array([])
    }))
    //console.log(this.clients)
  }

  addStore(client: FormGroup): void{
    this.getStores(client).push(this.fb.group({
      pool_point_store_number: [''],
      pool_point_store_address_uuid: ['', [Validators.required, uuidValidator()]],
      pool_point_store_contact_uuid: ['', [Validators.required, uuidValidator()]],
      pool_point_store_contact_name: ['', Validators.required], // for user readability
      pool_point_store_address_text: ['']
    }))
  }

  removeClient(index: number): void {
    this.clients.removeAt(index);
  }

  removeStore(client: FormGroup, index: number): void{
    let storesArray = client.get('stores') as FormArray;
    storesArray.removeAt(index);
  }

  /* Form Control Helper Functions */
  get poolPointContactControl(): FormControl {
    return this.poolPointForm.get('pool_point_contact_name') as FormControl;
  }

  getPoolPointClientContactControl(client: FormGroup): FormControl {
    return client.get('pool_point_client_contact_name') as FormControl;
  }

  getPoolPointStoreContactControl(store: FormGroup): FormControl {
    return store.get('pool_point_store_contact_name') as FormControl;
  }
}

// for fun
export function uuidValidator(): ValidatorFn{
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null; // don't validate empty, let `required` handle that

      const uuidV4Regex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

      return uuidV4Regex.test(control.value)
        ? null
        : { uuid: { value: control.value } };
    };
}